import { NodeTypes, QuestionTypes } from "./constants";
import {
  FlowField,
  NodeField,
  OptionField,
  NodeType,
  Handle,
  NODE_FIELD_SPEC,
  edgeStrokeForHandle,
  listHandleId,
  isListHandle,
} from "./flowSchema";

let nextId = 1;
const getId = () => `node_${nextId++}`;

export function resetIdCounter(startFrom = 1) {
  nextId = startFrom;
}

export function generateNodeId() {
  return getId();
}

const ROLE_BY_NODE_TYPE = {
  [NodeTypes.START]: NodeType.START,
  [NodeTypes.QUESTION]: NodeType.STEP,
  [NodeTypes.TERMINAL]: NodeType.END,
};

const NODE_TYPE_BY_ROLE = {
  [NodeType.START]: NodeTypes.START,
  [NodeType.STEP]: NodeTypes.QUESTION,
  [NodeType.END]: NodeTypes.TERMINAL,
};

const EDGE_VISUAL_DEFAULTS = {
  type: "smoothstep",
  labelStyle: { fontWeight: 600, fontSize: "12px" },
  labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
  labelBgPadding: [6, 4],
  labelBgBorderRadius: 4,
};

// A node's outgoing branches in stable order. Reconstructed by position, not by
// label text, so renaming a label never re-targets a branch.
function branchesFor(node, edges) {
  if (node.type === NodeTypes.TERMINAL) return [];

  const targetVia = (handle) => {
    const edge = edges.find(
      (e) => e.source === node.id && (e.sourceHandle ?? null) === (handle ?? null)
    );
    return edge ? edge.target : null;
  };

  const questionType = node.data?.questionType;

  if (node.type === NodeTypes.QUESTION && questionType === QuestionTypes.YES_NO) {
    return [
      { label: "Yes", nextNodeId: targetVia(Handle.YES) },
      { label: "No", nextNodeId: targetVia(Handle.NO) },
    ];
  }

  if (node.type === NodeTypes.QUESTION && questionType === QuestionTypes.LIST) {
    const options = node.data?.[NodeField.LIST_OPTIONS] || [];
    return options.map((label, i) => ({ label, nextNodeId: targetVia(listHandleId(i)) }));
  }

  // START / TEXT / DURATION — single "continue" path (no handle id on START).
  const handle = node.type === NodeTypes.START ? null : Handle.CONTINUE;
  return [{ label: null, nextNodeId: targetVia(handle) }];
}

export function serializeFlow(nodes, edges) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeEdges = Array.isArray(edges) ? edges : [];

  const startNode = safeNodes.find((n) => n.type === NodeTypes.START);

  const jsonNodes = safeNodes.map((node) => {
    const out = {
      [NodeField.ID]: node.id,
      [NodeField.TYPE]: ROLE_BY_NODE_TYPE[node.type] || NodeType.STEP,
      [NodeField.IS_EDGE_NODE]: node.type === NodeTypes.TERMINAL,
      [NodeField.POSITION_X]: node.position?.x ?? 0,
      [NodeField.POSITION_Y]: node.position?.y ?? 0,
    };
    NODE_FIELD_SPEC.forEach(({ key, default: fallback }) => {
      const value = node.data?.[key];
      out[key] = value === undefined ? fallback : value;
    });
    if (out[NodeField.TYPE] === NodeType.START || out[NodeField.TYPE] === NodeType.END) {
      out[NodeField.QUESTION_TYPE] = null;
      out[NodeField.SECTION] = null;
    }
    return out;
  });

  let optionId = 1;
  const nodeOptions = [];
  safeNodes.forEach((node) => {
    branchesFor(node, safeEdges).forEach((branch) => {
      nodeOptions.push({
        [OptionField.ID]: optionId++,
        [OptionField.NODE_ID]: node.id,
        [OptionField.LABEL]: branch.label,
        [OptionField.NEXT_NODE_ID]: branch.nextNodeId,
      });
    });
  });

  return {
    [FlowField.ROOT_NODE_ID]: startNode ? startNode.id : null,
    [FlowField.NODES]: jsonNodes,
    [FlowField.NODE_OPTIONS]: nodeOptions,
  };
}

export function deserializeFlow(jsonFlow) {
  const parsed = parseJsonFlow(jsonFlow);
  if (!parsed) return { nodes: [], edges: [] };

  const jsonNodes = Array.isArray(parsed[FlowField.NODES]) ? parsed[FlowField.NODES] : [];
  const jsonOptions = Array.isArray(parsed[FlowField.NODE_OPTIONS])
    ? parsed[FlowField.NODE_OPTIONS]
    : [];

  const optionsByNode = groupOptionsByNode(jsonOptions);

  const nodes = jsonNodes.map((jn) => {
    const data = {};
    NODE_FIELD_SPEC.forEach(({ key, default: fallback }) => {
      const value = jn[key];
      data[key] = value === undefined ? fallback : value;
    });
    if (data[NodeField.QUESTION_TYPE] === QuestionTypes.LIST) {
      data[NodeField.LIST_OPTIONS] = (optionsByNode.get(jn[NodeField.ID]) || []).map(
        (o) => o[OptionField.LABEL] ?? ""
      );
    } else {
      data[NodeField.LIST_OPTIONS] = [];
    }
    const type = NODE_TYPE_BY_ROLE[jn[NodeField.TYPE]] || NodeTypes.QUESTION;
    return {
      id: jn[NodeField.ID],
      type,
      position: {
        x: jn[NodeField.POSITION_X] ?? 0,
        y: jn[NodeField.POSITION_Y] ?? 0,
      },
      data,
      ...(type === NodeTypes.START ? { deletable: false } : {}),
    };
  });

  const questionTypeById = new Map(
    jsonNodes.map((n) => [n[NodeField.ID], n[NodeField.QUESTION_TYPE]])
  );
  const roleById = new Map(jsonNodes.map((n) => [n[NodeField.ID], n[NodeField.TYPE]]));

  let edgeSeq = 1;
  const edges = [];
  optionsByNode.forEach((options, nodeId) => {
    options.forEach((option, index) => {
      const nextNodeId = option[OptionField.NEXT_NODE_ID];
      if (nextNodeId === null || nextNodeId === undefined) return;

      const sourceHandle = handleFor(roleById.get(nodeId), questionTypeById.get(nodeId), index);
      edges.push(
        buildEdge({
          id: `edge_${edgeSeq++}`,
          source: nodeId,
          sourceHandle,
          target: nextNodeId,
          label: isListHandle(sourceHandle)
            ? undefined
            : option[OptionField.LABEL] || "Continue",
        })
      );
    });
  });

  syncIdCounter(nodes);
  return { nodes, edges };
}

function groupOptionsByNode(jsonOptions) {
  const byNode = new Map();
  jsonOptions
    .slice()
    .sort((a, b) => (a[OptionField.ID] ?? 0) - (b[OptionField.ID] ?? 0))
    .forEach((option) => {
      const nodeId = option[OptionField.NODE_ID];
      if (!byNode.has(nodeId)) byNode.set(nodeId, []);
      byNode.get(nodeId).push(option);
    });
  return byNode;
}

function handleFor(role, questionType, index) {
  if (role === NodeType.START) return undefined;
  if (questionType === QuestionTypes.YES_NO) return index === 0 ? Handle.YES : Handle.NO;
  if (questionType === QuestionTypes.LIST) return listHandleId(index);
  return Handle.CONTINUE;
}

function buildEdge(edge) {
  return {
    ...edge,
    ...EDGE_VISUAL_DEFAULTS,
    style: { stroke: edgeStrokeForHandle(edge.sourceHandle), strokeWidth: 2 },
  };
}

function parseJsonFlow(jsonFlow) {
  if (!jsonFlow) return null;
  if (typeof jsonFlow !== "string") return jsonFlow;
  try {
    return JSON.parse(jsonFlow);
  } catch (e) {
    return null;
  }
}

function syncIdCounter(nodes) {
  const maxNodeId = nodes.reduce((max, n) => {
    const match = String(n.id).match(/\d+/);
    return match ? Math.max(max, parseInt(match[0], 10)) : max;
  }, 0);
  resetIdCounter(maxNodeId + 1);
}
